/* eslint-disable no-nested-ternary */
import { useCallback, useMemo, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import {
    Select,
    Stack,
    Table,
    TextInput,
    Tooltip,
} from '@mantine/core';
import { Checkbox, Loading, Toggle } from '@carbon/react';

import styles from './AttendeesDataTable.module.scss';

import sharedMessages from 'Components/utils/sharedMessages';
import { useEventContext } from 'Screens/Dashboard/Events/EventHandler/EntityEventContext';
import { useGetTeamMembersQuery } from 'api/entityEndpoints';
import { useGetEventRolesQuery } from 'api/eventRoleEndpoints';
import useGetCurrentEntityId from 'hooks/useGetCurrentEntityId';
import { AttendeeStatus, EventAttendeeDto } from 'api/dtos/event';
import useGetUser from 'hooks/useGetUser';

type AttendeeRow = {
    id: string;
    name: string;
    tags: string[];
    required: boolean;
    isSelected: boolean;
    isSelfJoined: boolean;
    declineReason: string | undefined;
    status: AttendeeStatus | undefined;
    eventRoleId: string | undefined;
    eventRoleName: string | undefined;
}

type Props = {
    skip?: boolean;
}

const getAttendeeUserKey = (attendee: EventAttendeeDto) => attendee.userId || attendee.id || '';
const getElementId = (prefix: string, instanceId: string, rowId: string) => `${prefix}-${instanceId}-${rowId}`;

const AttendeesDataTable = ({ skip }: Props) => {
    const intl = useIntl();
    const [search, setSearch] = useState('');
    const currentUser = useGetUser();

    const { values, updateValues } = useEventContext();
    const isEdit = useMemo(() => values.isEdit, [values.isEdit]);
    const tableInstanceId = values.entityEventId ?? values.id ?? 'new-event';

    const title = isEdit
        ? intl.formatMessage(messages.attendeesTitleEdit)
        : intl.formatMessage(messages.attendeesTitleSelectRequired);

    const currentEntityId = useGetCurrentEntityId();
    const selectedEntityId = values.entityId ?? currentEntityId;
    const { data, isFetching: isFetchingMembers } = useGetTeamMembersQuery(selectedEntityId ?? '', { skip: !selectedEntityId || skip });
    const { data: eventRoles, isFetching: isFetchingEventRoles } = useGetEventRolesQuery(selectedEntityId ?? '', { skip: !selectedEntityId || skip });
    const isFetching = isFetchingMembers || isFetchingEventRoles;

    const selectedAttendees = useMemo(
        () => new Map(values.attendees
            .map((attendee) => [getAttendeeUserKey(attendee), attendee] as const)
            .filter(([key]) => key.length > 0)),
        [values.attendees],
    );

    const attendeesRows: AttendeeRow[] = useMemo(() => (data?.items ?? []).map((at) => {
        const isSelected = selectedAttendees.has(at.id);
        const selectedAttendee = selectedAttendees.get(at.id);
        const isSelfJoined = Boolean(
            isSelected && 
            !selectedAttendee?.isRequired && 
            !selectedAttendee?.isOrganizer &&
            selectedAttendee?.status === AttendeeStatus.Accepted,
        );
        return ({
            id: at.id,
            name: `${at.firstName} ${at.lastName}`,
            tags: at.tags.map((tag) => tag.name),
            required: Boolean(isSelected && selectedAttendee?.isRequired),
            isSelected,
            isSelfJoined,
            declineReason: selectedAttendee?.declineReason,
            status: selectedAttendee?.status,
            eventRoleId: selectedAttendee?.eventRoleId,
            eventRoleName: eventRoles?.find((role) => role.id === selectedAttendee?.eventRoleId)?.name,
        });
    }).sort((a, b) => {
        if (a.required && !b.required) return -1;
        if (b.required && !a.required) return 1;
        if (a.isSelected && !b.isSelected) return -1;
        if (b.isSelected && !a.isSelected) return 1;
        return 0;
    }), [data, eventRoles, selectedAttendees]);

    const filteredRows = useMemo(() => {
        const term = search.toLowerCase().trim();
        if (!term) return attendeesRows;
        return attendeesRows.filter((row) =>
            row.name.toLowerCase().includes(term) ||
        row.tags.some((tag) => tag.toLowerCase().includes(term)) ||
        row.eventRoleName?.toLowerCase().includes(term));
    }, [search, attendeesRows]);

    const allSelected = filteredRows.length > 0 && filteredRows.every((row) => row.isSelected);
    const partiallySelected = !allSelected && filteredRows.some((row) => row.isSelected);

    const setAttendeesRows = useCallback((attendees: EventAttendeeDto[]) => {
        updateValues({ attendees });
    }, [updateValues]);

    const toggleAll = () => {
        const visibleUserIds = new Set(filteredRows.map((row) => row.id));

        if (!allSelected) {
            const hiddenAttendees = values.attendees
                .filter((attendee) => !visibleUserIds.has(getAttendeeUserKey(attendee)));
            const visibleAttendees = filteredRows.map((row) => selectedAttendees.get(row.id) ?? ({
                userId: row.id,
                isRequired: true,
                isOrganizer: row.id === currentUser?.id,
                eventRoleId: row.eventRoleId,
            }));

            setAttendeesRows(hiddenAttendees.concat(visibleAttendees));
        } else {
            setAttendeesRows(values.attendees
                .filter((attendee) => !visibleUserIds.has(getAttendeeUserKey(attendee))));
        }
    };

    const toggleRow = (row: AttendeeRow) => {
        if (selectedAttendees.has(row.id)) {
            setAttendeesRows(values.attendees.filter((attendee) => getAttendeeUserKey(attendee) !== row.id));
            return;
        }
        setAttendeesRows(values.attendees.concat([{
            userId: row.id,
            isRequired: true,
            isOrganizer: row.id === currentUser?.id,
            eventRoleId: undefined,
        }]));
    };

    const setEventRole = (row: AttendeeRow, roleId: string) => {
        const resolvedRoleId = roleId === '' ? undefined : roleId;
        if (selectedAttendees.has(row.id)) {
            setAttendeesRows(values.attendees.map((attendee) =>
                (getAttendeeUserKey(attendee) === row.id ? { ...attendee, eventRoleId: resolvedRoleId } : attendee)));
        } else {
            setAttendeesRows(values.attendees.concat([{
                userId: row.id,
                isRequired: false,
                isOrganizer: row.id === currentUser?.id,
                eventRoleId: resolvedRoleId,
            }]));
        }
    };

    const rows = filteredRows.map((row) => (
        <Table.Tr
            key={`attendee-row-${row.id}`}
            bg={row.isSelected
                ? row.isSelfJoined
                    ? 'var(--mantine-color-green-light)'
                    : 'var(--mantine-color-blue-light)'
                : undefined}
        >
            <Table.Td>
                <Checkbox
                    id={getElementId('attendee-checkbox', tableInstanceId, row.id)}
                    checked={row.isSelected}
                    labelText=""
                    onChange={() => { toggleRow(row); }}
                />
            </Table.Td>
            <Table.Td>{row.name}</Table.Td>
            <Table.Td>
                {row.tags.reduce((prev, curr, i) => (i === 0 ? curr : `${prev}, ${curr}`), '')}
            </Table.Td>
            {isEdit && (
                <>
                    <Table.Td>
                        {row.isSelected
                            ? row.status === AttendeeStatus.Declined
                                ? intl.formatMessage(sharedMessages.yes)
                                : intl.formatMessage(sharedMessages.no)
                            : 'Nie dotyczy'}
                    </Table.Td>
                    <Table.Td>
                        {row.declineReason?.length
                            ? (
                                <Tooltip
                                    label={row.declineReason}
                                    position="top"
                                >
                                    <span className={styles.declineReason}>
                                        {row.declineReason.length > 30
                                            ? `${row.declineReason.slice(0, 30)}...`
                                            : row.declineReason}
                                    </span>
                                </Tooltip>
                            )
                            : '-'}
                    </Table.Td>
                </>
            )}
            <Table.Td>
                {row.isSelfJoined ? (
                    <span className={styles.selfJoinedBadge}>
                        {intl.formatMessage(messages.selfJoined)}
                    </span>
                ) : row.isSelected ? (
                    <Toggle
                        id={getElementId('required-toggle', tableInstanceId, row.id)}
                        labelText=""
                        hideLabel
                        toggled={row.required}
                        onToggle={(checked) => {
                            setAttendeesRows(values.attendees.map((attendee) =>
                                (getAttendeeUserKey(attendee) === row.id
                                    ? { ...attendee, isRequired: checked }
                                    : attendee)));
                        }}
                    />
                ) : null}
            </Table.Td>
            <Table.Td>
                <Select
                    id={getElementId('attendee-role-select', tableInstanceId, row.id)}
                    value={row.eventRoleId ?? ''}
                    disabled={!row.isSelected}
                    data={[
                        { value: '', label: intl.formatMessage(messages.noRole) },
                        ...(eventRoles ?? []).map((role) => ({
                            value: role.id,
                            label: role.name,
                        })),
                    ]}
                    onChange={(value) => { setEventRole(row, value ?? ''); }}
                    radius="md"
                    size="sm"
                    comboboxProps={{ withinPortal: false }}
                />
            </Table.Td>
        </Table.Tr>
    ));

    return (
        <div>
            <span className={styles.label}>{title}</span>
            <Stack>
                <TextInput
                    id={`search-attendee-table-${tableInstanceId}`}
                    placeholder={intl.formatMessage(messages.searchPlaceholder)}
                    value={search}
                    onChange={(e) => { setSearch(e.currentTarget.value); }}
                    radius="xl"
                    size="md"
                />
                {isFetching && <Loading small withOverlay={false} />}
                <Table withRowBorders withColumnBorders striped highlightOnHover>
                    <Table.Thead>
                        <Table.Tr>
                            <Table.Th>
                                <Checkbox
                                    id={`checkbox-all-in-attendees-data-table-${tableInstanceId}`}
                                    checked={allSelected}
                                    labelText=""
                                    indeterminate={partiallySelected}
                                    onChange={toggleAll}
                                />
                            </Table.Th>
                            <Table.Th>{intl.formatMessage(sharedMessages.nameAndSurname)}</Table.Th>
                            <Table.Th>{intl.formatMessage(sharedMessages.role)}</Table.Th>
                            {isEdit && (
                                <>
                                    <Table.Th>{intl.formatMessage(messages.declined)}</Table.Th>
                                    <Table.Th>{intl.formatMessage(messages.declineReason)}</Table.Th>
                                </>
                            )}
                            <Table.Th>{intl.formatMessage(sharedMessages.required)}</Table.Th>
                            <Table.Th>{intl.formatMessage(messages.eventRole)}</Table.Th>
                        </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>{rows}</Table.Tbody>
                </Table>
            </Stack>
        </div>
    );
};

const messages = defineMessages({
    attendeesTitleSelectRequired: {
        id: 'AttendeesDataTable.attendeesTitleSelectRequired',
        defaultMessage: 'Wyznacz osoby z obowiązkową obecnością',
    },
    attendeesTitleEdit: {
        id: 'AttendeesDataTable.attendeesTitleEdit',
        defaultMessage: 'Edytuj lub sprawdź kto będzie obecny',
    },
    declined: {
        id: 'AttendeesDataTable.declined',
        defaultMessage: 'Zrezygnował/a',
    },
    declineReason: {
        id: 'AttendeesDataTable.declineReason',
        defaultMessage: 'Powód rezygnacji',
    },
    eventRole: {
        id: 'AttendeesDataTable.eventRole',
        defaultMessage: 'Rola w wydarzeniu',
    },
    noRole: {
        id: 'AttendeesDataTable.noRole',
        defaultMessage: 'Brak roli',
    },
    selfJoined: {
        id: 'AttendeesDataTable.selfJoined',
        defaultMessage: 'Dołączył/a samodzielnie',
    },
    searchPlaceholder: {
        id: 'AttendeesDataTable.searchPlaceholder',
        defaultMessage: 'Przeszukaj po nazwie, roli lub roli w wydarzeniu',
    },
});

export default AttendeesDataTable;
